import { 
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  Timestamp,
  addDoc,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';

// Constants
const USERS_COLLECTION = 'users';
const ACCOUNT_USERS_COLLECTION = 'accountUsers';

// User document structure
export interface User {
  id: string;
  email: string;
  name?: string;
  createdAt: Timestamp;
  lastLoginAt: Timestamp;
  accounts: string[];
}

// Account user link structure
export interface AccountUser {
  id?: string;
  userId: string;
  accountId: string;
  status: 'active' | 'invited' | 'disabled';
  invitedBy: string;
  invitedAt: Timestamp;
  joinedAt?: Timestamp;
}

// Create or update user with accounts
export const createOrUpdateUser = async (userId: string, email: string, accounts: string[]) => {
  try {
    const batch = writeBatch(db);
    const userRef = doc(db, USERS_COLLECTION, userId);
    const existingAccountUsers = await getDocs(
      query(
        collection(db, ACCOUNT_USERS_COLLECTION),
        where('userId', '==', userId)
      )
    );

    // Get existing account links
    const existingAccounts = new Set(
      existingAccountUsers.docs.map(doc => doc.data().accountId)
    );
    
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // Create new user
      const userData = {
        email,
        accounts,
        createdAt: Timestamp.now(),
        lastLoginAt: Timestamp.now()
      };
      batch.set(userRef, userData);
      console.log('Creating new user:', { userId });
    } else {
      // Update existing user
      const existingAccounts = userDoc.data().accounts || [];
      const updatedAccounts = [...new Set([...existingAccounts, ...accounts])];
      
      const updateData = {
        accounts: updatedAccounts,
        lastLoginAt: Timestamp.now()
      };
      batch.update(userRef, updateData);
      // console.log('Updating existing user:', { userId });
    }

    // Create account user links
    for (const accountId of accounts) {
      // Skip if account link already exists
      if (existingAccounts.has(accountId)) {
        // console.log('Account user link already exists for account');
        continue;
      }

      const accountUserRef = doc(collection(db, ACCOUNT_USERS_COLLECTION));
      const accountUserData = {
        userId,
        accountId,
        status: 'active',
        invitedBy: email,
        invitedAt: Timestamp.now(),
        joinedAt: Timestamp.now()
      };
      batch.set(accountUserRef, accountUserData);
      // console.log('Creating account user link for account');
    }

    await batch.commit();
    // console.log('Batch commit successful');
    return true;
  } catch (error) {
    console.error('Error in createOrUpdateUser:', error);
    throw error;
  }
};

export const getUser = async (userId: string) => {
  try {
    const docRef = await getDoc(doc(db, USERS_COLLECTION, userId));
    // Verbose debug log - commented for production
    // console.log('Retrieved user:', docRef.exists() ? 'User found' : 'User not found');
    return docRef.exists() ? { id: docRef.id, ...docRef.data() } as User : null;
  } catch (error) {
    console.error('Error in getUser:', error);
    throw error;
  }
};

// Update user's last login and return accounts
export const updateUserLastLogin = async (userId: string): Promise<string[]> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.log('User not found:', userId);
      return [];
    }

    const userData = userDoc.data();
    await updateDoc(userRef, { 
      lastLoginAt: Timestamp.now()
    });
    
    // console.log('Updated user last login:', userId);
    return userData.accounts || [];
  } catch (error) {
    console.error('Error in updateUserLastLogin:', error);
    throw error;
  }
};

// Account Users
export const createAccountUser = async (data: AccountUser) => {
  const accountUserRef = await addDoc(collection(db, 'accountUsers'), data);
  return { id: accountUserRef.id, ...data };
};

export const getAccountUsers = async (accountId: string) => {
  const q = query(collection(db, 'accountUsers'), where('accountId', '==', accountId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as AccountUser);
};

export const getUserAccounts = async (userId: string): Promise<string[]> => {
  const q = query(
    collection(db, 'accountUsers'), 
    where('userId', '==', userId)
  );
  const snapshot = await getDocs(q);
  const accounts = snapshot.docs
    .filter(doc => doc.data().status === 'active')
    .map(doc => doc.data().accountId);
  
  return [...new Set(accounts)];
};

// Check if user is admin for any account
export const checkAdminAccess = async (email: string): Promise<string[]> => {
  const q = query(
    collection(db, 'accountUsers'),
    where('email', '==', email.toLowerCase())
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .filter(doc => doc.data().status === 'active')
    .map(doc => doc.data().accountId);
};

// Check if user has any pending invitations
export const checkPendingInvitations = async (email: string): Promise<AccountUser[]> => {
  const q = query(collection(db, 'accountUsers'),
    where('email', '==', email.toLowerCase()),
    where('status', '==', 'invited')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as AccountUser);
};

// Accept invitation
export const acceptInvitation = async (invitationId: string, userId: string) => {
  const invitationRef = doc(db, 'accountUsers', invitationId);
  await updateDoc(invitationRef, {
    status: 'active',
    userId,
    joinedAt: Timestamp.now()
  });
};